/**
 * Chat History Component
 *
 * Scrollable list of chat messages with auto-scroll to bottom.
 */

import { type Component, createEffect, For, onMount, Show } from 'solid-js';
import { useAssistant } from './AssistantProvider';
import { ChatMessage } from './ChatMessage';

export const ChatHistory: Component = () => {
  const { state } = useAssistant();
  let containerRef: HTMLDivElement | undefined;

  // Auto-scroll to bottom when new messages arrive
  createEffect(() => {
    const messages = state().messages;
    if (messages.length > 0 && containerRef) {
      // Small delay to allow DOM update
      setTimeout(() => {
        containerRef?.scrollTo({
          top: containerRef.scrollHeight,
          behavior: 'smooth',
        });
      }, 50);
    }
  });

  // Scroll to bottom on mount if there are messages
  onMount(() => {
    if (state().messages.length > 0 && containerRef) {
      containerRef.scrollTop = containerRef.scrollHeight;
    }
  });

  return (
    <div
      ref={containerRef}
      class="flex-1 overflow-y-auto p-4 space-y-4"
      style={{ 'scroll-behavior': 'smooth' }}
    >
      <Show
        when={state().messages.length > 0}
        fallback={
          <div class="h-full flex flex-col items-center justify-center text-gray-400">
            <div class="w-16 h-16 mb-4 rounded-full bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center">
              <svg class="w-8 h-8 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
            </div>
            <h3 class="text-lg font-medium text-gray-300 mb-2">AI Assistant</h3>
            <p class="text-center max-w-xs">
              Ask me anything about your projects, agents, or workflows. I can help you create, configure, and manage
              everything in Aigents Work.
            </p>
            <div class="mt-6 space-y-2 text-sm">
              <p class="text-gray-500">Try asking:</p>
              <ul class="space-y-1 text-gray-400">
                <li>"Create a new project for my blog"</li>
                <li>"List all my agents"</li>
                <li>"Help me set up a content workflow"</li>
              </ul>
            </div>
          </div>
        }
      >
        <For each={state().messages}>{(message) => <ChatMessage message={message} />}</For>
      </Show>

      {/* Error display */}
      <Show when={state().error}>
        <div class="bg-red-900/30 border border-red-700/50 rounded-lg p-3 text-red-300 text-sm">
          <div class="flex items-center gap-2 mb-1">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            Error
          </div>
          <p>{state().error}</p>
        </div>
      </Show>
    </div>
  );
};
